import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Network, Plus } from 'lucide-react';

import { scenariosService } from '@/services/demandPlanning/scenarios.service';

import { CreateScenarioForm } from '../components/CreateScenarioForm';
import { demandPlanningPageClass } from '../utils/pageShell';
import type { CreateScenarioPayload } from '../types/consensus.types';

import { formatUiDate } from '@/utils/uiLocale';

function formatDate(value: string, language?: string | null): string {
  return formatUiDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' }, language);
}

export function ScenariosPage({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [planningCycleId, setPlanningCycleId] = useState<number | ''>('');

  const planningCycles = useQuery({
    queryKey: ['planning-cycles-list'],
    queryFn: () => scenariosService.listPlanningCycles(),
  });

  const scenarios = useQuery({
    queryKey: ['scenarios-list'],
    queryFn: () => scenariosService.list(),
  });

  const planVersions = useQuery({
    queryKey: ['plan-versions-for-scenario', planningCycleId],
    enabled: planningCycleId !== '',
    queryFn: () => scenariosService.listPlanVersions(planningCycleId as number),
  });

  const createScenario = useMutation({
    mutationFn: async (payload: CreateScenarioPayload) => {
      if (!planningCycleId) throw new Error('Planning cycle required');
      const versions = planVersions.data ?? [];
      const versionId = versions.find((v) => v.is_current)?.id ?? versions[0]?.id;
      if (!versionId) throw new Error('No plan version for cycle');
      await scenariosService.create({
        ...payload,
        planning_cycle: planningCycleId as number,
        base_plan_version: versionId,
      });
    },
    onSuccess: () => {
      setShowForm(false);
      void queryClient.invalidateQueries({ queryKey: ['scenarios-list'] });
      void queryClient.invalidateQueries({ queryKey: ['consensus-overview'] });
    },
  });

  const rows = scenarios.data ?? [];

  return (
    <div className={`${demandPlanningPageClass(embedded)} pt-4 md:pt-5`}>
      {!embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{t('sopScenarios.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">{t('sopScenarios.subtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/demand-planning/sop/plan" className="text-stocker-primary hover:underline">
              {t('consensus.title')}
            </Link>
            <Link to="/demand-planning/sop/cycles" className="text-stocker-primary hover:underline">
              {t('nav.planningCycles')}
            </Link>
            <span className="text-gray-500">{t('nav.scenarios')}</span>
          </div>
        </div>
      ) : null}

      <section className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-stocker-primary" />
            <h2 className="text-sm font-semibold text-gray-900">{t('sopScenarios.listTitle')}</h2>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-3.5 w-3.5" />
            {t('consensus.newScenario')}
          </button>
        </div>

        {showForm ? (
          <div className="border-b border-gray-100">
            <div className="px-4 py-3">
              <label className="flex max-w-md flex-col gap-1 text-xs text-gray-600">
                {t('consensus.planningCycle')}
                <select
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={planningCycleId}
                  onChange={(e) =>
                    setPlanningCycleId(e.target.value ? Number(e.target.value) : '')
                  }
                >
                  <option value="">{t('consensus.selectPlanningCycleForScenario')}</option>
                  {(planningCycles.data ?? []).map((cycle) => (
                    <option key={cycle.id} value={cycle.id}>
                      {cycle.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {planningCycleId ? (
              <CreateScenarioForm
                saving={createScenario.isPending}
                onCancel={() => setShowForm(false)}
                onSubmit={async (payload) => {
                  await createScenario.mutateAsync(payload);
                }}
              />
            ) : (
              <p className="px-4 py-3 text-sm text-gray-500">
                {t('consensus.selectPlanningCycleToCreateScenario')}
              </p>
            )}
          </div>
        ) : null}

        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('consensus.scenarioName')}</th>
                <th className="px-3 py-2">{t('sopScenarios.scenarioType')}</th>
                <th className="px-3 py-2">{t('consensus.cycleStatusLabel')}</th>
                <th className="px-3 py-2 text-right">{t('consensus.scenarioMultiplier')}</th>
                <th className="px-3 py-2">{t('consensus.comments')}</th>
                <th className="px-3 py-2">{t('consensus.created')}</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                    {t('sopScenarios.noScenarios')}
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`border-t border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                  >
                    <td className="px-3 py-2.5 font-medium text-gray-900">{row.name}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-600">{row.scenario_type}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-600">{row.status}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-gray-600">
                      {row.parameters_json?.multiplier ?? '—'}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{row.description || '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {formatDate(row.created_at, i18n.language)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
